import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const ACCESS_KEY = "rl_token";
const REFRESH_KEY = "rl_refresh";

// Storage helpers — try localStorage first (persistent / remember-me), fall back to
// sessionStorage (this-session-only). Either is fine; the read path tries both.
function getStored(key) {
  try { return localStorage.getItem(key) || sessionStorage.getItem(key) || null; }
  catch { return null; }
}
function storeTokens(access, refresh, remember) {
  try { localStorage.removeItem(ACCESS_KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
  try { sessionStorage.removeItem(ACCESS_KEY); } catch { /* ignore */ }
  try { sessionStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
  const store = localStorage; // always persist across refresh + tab close (all roles)
  try {
    if (access) store.setItem(ACCESS_KEY, access);
    if (refresh) store.setItem(REFRESH_KEY, refresh);
  } catch { /* ignore */ }
}
function clearTokens() {
  try { localStorage.removeItem(ACCESS_KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
  try { sessionStorage.removeItem(ACCESS_KEY); } catch { /* ignore */ }
  try { sessionStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
}
export const tokenStore = { getAccess: () => getStored(ACCESS_KEY), getRefresh: () => getStored(REFRESH_KEY), store: storeTokens, clear: clearTokens };

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // Client-side safety net so the UI never hangs on "loading" when the network
  // stalls or a downstream 3rd-party (Resend, Stripe) blocks the endpoint.
  // Individual callers can override per-request with a longer/shorter timeout.
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const t = getStored(ACCESS_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // Iter235 — attach the festival helper token (if any) so the backend
  // can resolve the caller's per-festival role. Scoped by festival_id
  // in the token itself; safe to send on every request.
  try {
    const raw = localStorage.getItem("rl_festival_helper_token");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.token) {
        // Only attach when hitting the matching festival — belt+braces
        // against a stale token leaking to unrelated calls.
        const url = config.url || "";
        const fid = parsed.festival_id;
        if (fid && (url.includes(`/festival/${fid}`) || url.startsWith(`/festival/${fid}`))) {
          config.headers["X-Festival-Helper-Token"] = parsed.token;
        }
      }
    }
  } catch { /* ignore */ }
  return config;
});

// --- Transparent token refresh ---
// On a 401, try ONCE to mint a new access token. We pass the refresh token in BOTH the
// cookie (auto by withCredentials) and the Authorization header — backend tries both.
// This is what keeps "Remember me" working even when Safari ITP / Chrome's third-party
// cookie phase-out blocks the cookie path.
let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  const refreshTok = getStored(REFRESH_KEY);
  refreshPromise = axios
    .post(`${API}/auth/refresh`, {}, {
      withCredentials: true,
      headers: refreshTok ? { Authorization: `Bearer ${refreshTok}` } : {},
      timeout: 12000,
    })
    .then((r) => {
      const access = r.data?.access_token;
      const refresh = r.data?.refresh_token || refreshTok;
      const remember = r.data?.remember !== false;
      if (access) storeTokens(access, refresh, remember);
      return access;
    })
    .catch((e) => {
      // Iter94e — Only clear tokens on a genuine auth failure (401 / 403).
      // Network errors, timeouts, or 5xx are almost always transient (Safari
      // suspending JS during navigation, backend restart, VPN blip) — wiping
      // tokens there caused the "logged out after minutes" bug because a
      // single flaky request during backgrounding killed the session.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        clearTokens();
      }
      throw e;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    const url = original?.url || "";
    // Iter441/442 — Helper-PIN single-device kickout. When the
    // backend rejects a helper token because someone else redeemed
    // the same PIN on another device, the response detail carries
    // the phrase "used on another device". Wipe the helper token,
    // toast the reason, and hard-redirect to the redeem screen if
    // we can recover the festival_id + pass_id from the stored
    // session. Detects on ANY authed helper endpoint so an idle
    // OPS Assist page picks up the kickout on its next 5-second
    // poll.
    try {
      // eslint-disable-next-line no-console
      // Iter442 · Diagnose — verbose console trace on every 401 so
      // we can see in Safari dev tools exactly which requests are
      // returning 401 and what the backend detail says. Remove
      // once the kickout is confirmed reaching the phone.
      // (Iter446 — kickout branch now handled below; the generic
      // 401 trace stays silent to keep production consoles clean.)
      const detail = String(error?.response?.data?.detail || "");
      if (status === 401 && /used on another device/i.test(detail)) {
        // Iter446 — Flag the error so page-level catch blocks can
        // skip rendering their own "something went wrong" banner
        // (we're about to tear the page down with a hard redirect;
        // flashing a red error strip for 150ms is messy).
        try { error.__helperKickout = true; } catch { /* ignore */ }
        // ─── 1. Resolve the best redirect target BEFORE wiping ─
        let redirect = "/login";
        try {
          const raw = localStorage.getItem("rl_festival_helper_token");
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.festival_id) {
              redirect = parsed.pass_id
                ? `/festival/${parsed.festival_id}/helper/${parsed.pass_id}`
                : `/festival/${parsed.festival_id}`;
            }
          }
        } catch { /* ignore */ }
        if (redirect === "/login" && typeof window !== "undefined") {
          const m = window.location.pathname.match(/\/festival\/([^/]+)/);
          if (m) redirect = `/festival/${m[1]}`;
        }
        // ─── 2. Wipe the stale helper token ──────────────────────
        try { localStorage.removeItem("rl_festival_helper_token"); } catch { /* ignore */ }
        // ─── 3. Friendly amber toast — NOT an error toast. Users
        //        read this once on their way to the redeem screen;
        //        a red "error" bar implies something is broken.
        try {
          toast.warning(
            "You have been signed out because this PIN was used on another device.",
            { duration: 5000 },
          );
        } catch { /* ignore */ }
        // ─── 4. Hard redirect with stacked fallbacks ─────────────
        if (typeof window !== "undefined") {
          const stillOnStalePage = window.location.pathname !== redirect;
          try {
            if (stillOnStalePage) {
              window.location.replace(redirect);
            } else {
              window.location.reload();
            }
          } catch {
            try { window.location.href = redirect; } catch { /* ignore */ }
          }
          try {
            setTimeout(() => {
              try {
                if (window.location.pathname !== redirect) {
                  window.location.href = redirect;
                } else {
                  window.location.reload();
                }
              } catch { /* ignore */ }
            }, 150);
          } catch { /* ignore */ }
        }
        return Promise.reject(error);
      }
    } catch (interceptorErr) {
      // eslint-disable-next-line no-console
      console.log("[iter442] interceptor threw", interceptorErr);
    }
    // Iter312 — Signed-in-elsewhere handler. Backend flags a displaced
    // session with `X-Session-Revoked: 1` on the 401 response. Wipe
    // tokens immediately, tell the user what happened, and hard-reload
    // to /login so no stale in-memory state (AuthContext user) survives.
    const revoked = error?.response?.headers?.["x-session-revoked"] === "1";
    if (revoked) {
      clearTokens();
      try {
        toast.error("Your account has been signed in on another device. Please log in again.", { duration: 6000 });
      } catch { /* ignore */ }
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
      return Promise.reject(error);
    }
    // Iter320 — Global 402 handler. Every 402 across the app surfaces
    // as a clean toast with the backend's human detail — NEVER as a
    // raw AxiosError. Individual callers that render their own
    // full-screen notice can opt out with `{ __suppress402: true }`.
    if (status === 402) {
      if (!original?.__suppress402) {
        try {
          const detail = error?.response?.data?.detail
            || "This feature isn't available on your current plan.";
          toast.error(detail, { duration: 6000 });
        } catch { /* ignore */ }
      }
      // Return a resolved-null so `try { await api.get(...) } catch (e) {}`
      // paths don't swallow-then-rethrow a raw axios error. The caller
      // sees `undefined` and can branch on it. Components that want
      // the raw error can pass `__preserve402: true`.
      if (original?.__preserve402) return Promise.reject(error);
      return Promise.resolve({ data: null, status: 402, __handled402: true, __detail: error?.response?.data?.detail });
    }
    // Only auto-refresh on a real 401 against an authenticated endpoint, and only once.
    if (
      status === 401 &&
      original &&
      !original._retried &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/refresh") &&
      !url.includes("/auth/register")
    ) {
      original._retried = true;
      try {
        const newTok = await refreshAccessToken();
        if (newTok) {
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newTok}` };
          return api(original);
        }
      } catch (_e) { /* fall through to reject */ }
    }
    return Promise.reject(error);
  }
);

// Iter320 — Also install a 402 interceptor on the DEFAULT axios export
// so raw `axios.get(...)` calls (used on player-facing pages that
// don't go through the shared `api` instance) get the same graceful
// treatment. Components can opt-out with `{ __suppress402: true }`
// when they want to render their own full-screen unavailable notice.
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const original = error?.config;
    if (status === 402) {
      if (!original?.__suppress402) {
        try {
          const detail = error?.response?.data?.detail
            || "This feature isn't available on your current plan.";
          toast.error(detail, { duration: 6000 });
        } catch { /* ignore */ }
      }
      if (original?.__preserve402) return Promise.reject(error);
      return Promise.resolve({ data: null, status: 402, __handled402: true, __detail: error?.response?.data?.detail });
    }
    return Promise.reject(error);
  }
);

export function formatApiError(errOrDetail, fallback = "Something went wrong.") {
  // Iter435 — Accept either a raw detail value or an axios/Error
  // object. Callers across the app pass `formatApiError(e, "…")` to
  // extract the backend's `detail` field; the OPS Assistant toast
  // was leaking the raw "AxiosError: Request failed with status
  // code 400" string because we previously only handled the detail
  // form. Extract in this priority order:
  //   1. err.response.data.detail (FastAPI HTTPException shape)
  //   2. err.response.data (plain-string body)
  //   3. err.message (network / axios timeout)
  //   4. String coerce
  const isAxiosLike = errOrDetail && typeof errOrDetail === "object" && (errOrDetail.isAxiosError || errOrDetail.response || errOrDetail.message);
  if (isAxiosLike) {
    const detail = errOrDetail?.response?.data?.detail;
    if (detail != null) {
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
      if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
      return String(detail);
    }
    const body = errOrDetail?.response?.data;
    if (typeof body === "string" && body.trim()) return body;
    if (errOrDetail?.message && !/^AxiosError/i.test(errOrDetail.message)) return errOrDetail.message;
    return fallback;
  }
  const detail = errOrDetail;
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
